// One menu for the whole app (DESIGN-SPEC §6.9; POLISH-BACKLOG PL-21a and PL-31).
//
// A thin layer over Radix DropdownMenu, so Esc, outside click, arrow keys,
// typeahead and focus return all come from one place. `OverflowMenu` is the
// "⋯" menu that holds a region's secondary actions: one visible primary per
// region, everything else one step away. Its props match the ⋯ menu in the
// actions visual layer (label, items, className) so the two converge.
//
// A menu never carries an approval. Decisions stay on their own row or in the
// object's canvas footer (DESIGN-SPEC §7).
//
// Menus portal into the workspace shell their trigger sits in, so they inherit
// its palette (light or dark) instead of carrying a copy of it; a trigger
// outside the shell (none today) portals to <body> and gets the global palette.
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { DropdownMenu } from 'radix-ui';
import { CheckIcon, EllipsisIcon } from 'lucide-react';
import { ActionButton } from '@/components/assistant-ui/action-button';
import { overlaysCopy } from '@/i18n/ns/overlays';
import { useNamespaceCopy } from '@/i18n/ns';
import './overlays.css';

type OpenState = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Where the content portals: the trigger's workspace shell, or null for <body>. */
  host: HTMLElement | null;
  setAnchor: (trigger: HTMLElement | null) => void;
};
const MenuOpenContext = createContext<OpenState | null>(null);

/** The element a menu portals into, found from its trigger. */
const PORTAL_HOST = '.ws-shell';

export interface MenuProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Non-modal by default: an item may open a dialog, and the page stays scrollable. */
  modal?: boolean;
}

export function Menu({ children, open: controlled, onOpenChange, modal = false }: MenuProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const setOpen = useCallback(
    (next: boolean) => {
      if (controlled === undefined) setUncontrolled(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );
  const [host, setHost] = useState<HTMLElement | null>(null);
  const setAnchor = useCallback((trigger: HTMLElement | null) => {
    if (trigger) setHost(trigger.closest<HTMLElement>(PORTAL_HOST));
  }, []);
  const state = useMemo(
    () => ({ open, setOpen, host, setAnchor }),
    [open, setOpen, host, setAnchor],
  );
  return (
    <MenuOpenContext.Provider value={state}>
      <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={modal}>
        {children}
      </DropdownMenu.Root>
    </MenuOpenContext.Provider>
  );
}

type TriggerProps = ComponentPropsWithoutRef<typeof DropdownMenu.Trigger>;

/**
 * Radix opens a menu on pointer down and on Enter, Space or ArrowDown. A click
 * that arrives with neither (a screen reader's virtual click, element.click())
 * would do nothing, so it toggles the menu here.
 */
export const MenuTrigger = forwardRef<HTMLButtonElement, TriggerProps>(function MenuTrigger(
  { onPointerDown, onClick, ...props },
  ref,
) {
  const menu = useContext(MenuOpenContext);
  const pointer = useRef(false);
  const setAnchor = menu?.setAnchor;
  const anchor = useCallback(
    (node: HTMLButtonElement | null) => {
      setAnchor?.(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref, setAnchor],
  );
  return (
    <DropdownMenu.Trigger
      ref={anchor}
      {...props}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        pointer.current = event.button === 0 && !event.ctrlKey;
      }}
      onClick={(event) => {
        onClick?.(event);
        const virtual = !pointer.current && !event.detail;
        pointer.current = false;
        if (virtual && !event.defaultPrevented && menu) menu.setOpen(!menu.open);
      }}
    />
  );
});

type ContentProps = Omit<ComponentPropsWithoutRef<typeof DropdownMenu.Content>, 'aria-label'> & {
  /** The accessible name of the menu ("Chat access", "More actions for Erik"). */
  label: string;
};

export function MenuContent({
  label,
  className = '',
  align = 'end',
  sideOffset = 8,
  children,
  ...props
}: ContentProps) {
  const host = useContext(MenuOpenContext)?.host ?? undefined;
  return (
    <DropdownMenu.Portal container={host}>
      <DropdownMenu.Content
        {...props}
        aria-label={label}
        className={`pc-menu pc-portal ${className}`.trim()}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={16}
        loop
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

export type MenuTone = 'danger';

type ItemProps = Omit<ComponentPropsWithoutRef<typeof DropdownMenu.Item>, 'onSelect'> & {
  icon?: ReactNode;
  /** One muted line under the label (a consequence, never a repeat of it). */
  description?: string;
  tone?: MenuTone;
  /** Why the action is unavailable. The item stays focusable and says so. */
  reason?: string;
  onSelect?: (event: Event) => void;
};

function ItemText({ children, description }: { children: ReactNode; description?: string }) {
  return (
    <span className="pc-menu-item-text">
      <span className="pc-menu-item-label">{children}</span>
      {description && <span className="pc-menu-item-description">{description}</span>}
    </span>
  );
}

export function MenuItem({
  icon,
  description,
  tone,
  reason,
  onSelect,
  className = '',
  children,
  ...props
}: ItemProps) {
  return (
    <DropdownMenu.Item
      {...props}
      // Radix spreads these after its own aria-disabled, so only add the key when it applies.
      {...(reason ? { 'aria-disabled': true } : {})}
      className={`pc-menu-item${tone === 'danger' ? ' is-danger' : ''} ${className}`.trim()}
      onSelect={(event) => {
        if (reason) event.preventDefault();
        else onSelect?.(event);
      }}
    >
      {icon}
      <ItemText description={reason || description}>{children}</ItemText>
    </DropdownMenu.Item>
  );
}

export const MenuRadioGroup = DropdownMenu.RadioGroup;

type RadioItemProps = ComponentPropsWithoutRef<typeof DropdownMenu.RadioItem> & {
  icon?: ReactNode;
  description?: string;
};

/** A choice with a check on the right when it is the current one. */
export function MenuRadioItem({
  icon,
  description,
  className = '',
  children,
  ...props
}: RadioItemProps) {
  return (
    <DropdownMenu.RadioItem {...props} className={`pc-menu-item ${className}`.trim()}>
      {icon}
      <ItemText description={description}>{children}</ItemText>
      <DropdownMenu.ItemIndicator className="pc-menu-check">
        <CheckIcon aria-hidden="true" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.RadioItem>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="pc-menu-separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className="pc-menu-label">{children}</DropdownMenu.Label>;
}

/** A quiet, non-interactive line in a menu: what a choice means, not another choice. */
export function MenuNote({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="pc-menu-note">
      {icon}
      <span>{children}</span>
    </div>
  );
}

/** The ⋯ trigger: a quiet 28 px square (44 on touch) with a name and a tooltip. */
export const MoreButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'> & { label: string }
>(function MoreButton({ label, className = '', ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={`pc-more ${className}`.trim()}
      aria-label={label}
      title={label}
    >
      <EllipsisIcon aria-hidden="true" />
    </button>
  );
});

export type OverflowItem = {
  key: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  tone?: MenuTone;
  disabled?: boolean;
  reason?: string;
  /** Start a new group: a separator goes above this item (destructive items go last). */
  separated?: boolean;
  onSelect: () => void;
};

/**
 * The region's "⋯": secondary actions behind one quiet trigger. Renders nothing
 * when there is nothing to hide, so a region never shows an empty menu.
 */
export function OverflowMenu({
  label,
  items,
  className = '',
  menuLabel,
  align = 'end',
  variant = 'quiet',
  note,
  reserveSlot = false,
}: {
  /** The trigger's accessible name, e.g. "More actions for Erik Johansson". */
  label?: string;
  items: OverflowItem[];
  className?: string;
  menuLabel?: string;
  align?: ContentProps['align'];
  /** `secondary` among buttons (a bordered square as tall as its row); `quiet` in rows. */
  variant?: 'quiet' | 'secondary';
  /** Detail that is useful but not needed at a glance (a diagnostic), last and quiet. */
  note?: string;
  /** Keep the ⋯ column when there is nothing to hide, so a list's trailing column lines up. */
  reserveSlot?: boolean;
}) {
  const copy = useNamespaceCopy(overlaysCopy);
  if (!items.length)
    return reserveSlot ? <span className="pc-more-slot" aria-hidden="true" /> : null;
  const name = label ?? copy.moreActions;
  return (
    <Menu>
      <MenuTrigger asChild>
        <MoreButton label={name} className={className} data-variant={variant} />
      </MenuTrigger>
      <MenuContent label={menuLabel ?? name} align={align}>
        {items.map((item) => (
          <OverflowMenuEntry key={item.key} item={item} />
        ))}
        {note && (
          <>
            <MenuSeparator />
            <MenuNote>{note}</MenuNote>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

function OverflowMenuEntry({ item }: { item: OverflowItem }) {
  return (
    <>
      {item.separated && <MenuSeparator />}
      <MenuItem
        icon={item.icon}
        description={item.description}
        tone={item.tone}
        disabled={item.disabled}
        reason={item.reason}
        onSelect={item.onSelect}
      >
        {item.label}
      </MenuItem>
    </>
  );
}

/**
 * A row's secondary actions (DESIGN-SPEC §6.6): one action stays a visible quiet
 * button, two or more fold into ⋯. A ⋯ that would hold a single item only adds a click.
 */
export function splitRowActions(items: OverflowItem[]) {
  return {
    single: items.length === 1 ? items[0] : null,
    menu: items.length > 1 ? items : [],
  };
}

/**
 * The single quiet action of a row. With a pointer that hovers it shows on the
 * row's hover or focus (the row carries `pc-reveal-host`); on touch it is always
 * shown. An unavailable action stays focusable, says why in its tooltip, and does
 * nothing (DESIGN-SPEC §6.1).
 */
export function RowAction({ item, describedBy }: { item: OverflowItem; describedBy?: string }) {
  return (
    <ActionButton
      variant="quiet"
      className="pc-row-action pc-reveal"
      data-tone={item.tone}
      disabled={item.disabled}
      aria-disabled={item.reason ? true : undefined}
      aria-describedby={describedBy}
      title={item.reason ?? item.description}
      onClick={() => {
        if (!item.reason) item.onSelect();
      }}
    >
      {item.label}
    </ActionButton>
  );
}
